"use client";

import { colors, misc, util } from "@/common/theme";
import BorderedIcon from "@/components/BorderedIcon";
import { setTypography } from "@/mono/lib/styles/mixins";
import { mediaMobile } from "@/mono/lib/responsive";
import { easeInOutExpo } from "@/mono/lib/styles/easing";
import styled, { css } from "styled-components";
import { useCallback, useMemo, useRef, useState } from "react";
import FormField from "./FormField";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(field, value) {
  if (field.required) {
    if (field.fieldType === "checkbox") {
      if (!Array.isArray(value) || value.length === 0)
        return "Selecteer minimaal één optie";
    } else if (!value || (typeof value === "string" && !value.trim())) {
      return field.fieldType === "select"
        ? "Maak een keuze"
        : "Dit veld is verplicht";
    }
  }

  if (field.fieldType === "email" && value && !EMAIL_REGEX.test(value)) {
    return "Controleer je emailadres";
  }

  return null;
}

function validateAllFields(fields, values) {
  const errors = {};
  let hasErrors = false;

  for (const field of fields) {
    const error = validateField(field, values[field.name]);
    if (error) {
      errors[field.name] = error;
      hasErrors = true;
    }
  }

  return { errors, hasErrors };
}

const AsteriskSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1142.2 1080">
    <path
      d="M123.3 898.3 379.8 1080l192.1-312.1 193.2 311 255.5-182.2-238.9-280.2 360.5-88.1-101.8-304.9L704 364.4 727.7 0 412.8.6 437.6 365 101.3 225.7 0 530.6l361.1 87z"
      fill="currentColor"
    />
  </svg>
);

export default function FormSection({ data }) {
  const {
    title,
    subtitle,
    ctaText = "Verzenden",
    successTitle = "Bedankt!",
    successMessage = "",
    formName = "contact",
    formFields = [],
  } = data;

  const fields = formFields;

  const [values, setValues] = useState(() => {
    const initial = {};
    for (const field of fields) {
      initial[field.name] = field.fieldType === "checkbox" ? [] : "";
    }
    return initial;
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [touched, setTouched] = useState({});

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors],
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
    setSubmitError(false);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      const { name } = e.target;
      setTouched((prev) => ({ ...prev, [name]: true }));
      const field = fields.find((f) => f.name === name);
      if (field) {
        const error = validateField(field, values[name]);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [fields, values],
  );

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();

      const { errors: newErrors, hasErrors: invalid } = validateAllFields(
        fields,
        values,
      );
      setErrors(newErrors);

      const allTouched = {};
      for (const field of fields) {
        allTouched[field.name] = true;
      }
      setTouched(allTouched);

      if (invalid) return;

      const formData = new URLSearchParams();
      formData.append("form-name", formName);
      for (const field of fields) {
        const val = values[field.name];
        formData.append(
          field.name,
          Array.isArray(val) ? val.join(", ") : val || "",
        );
      }

      try {
        const response = await fetch("/__forms.html", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formData.toString(),
        });

        if (!response.ok) throw new Error("Submit failed");
        setSubmitted(true);
        setSubmitError(false);
      } catch {
        setSubmitError(true);
      }
    },
    [fields, values, formName],
  );

  const cleanTitle = useMemo(
    () => title?.replace(/<\/?p>/g, "") || "",
    [title],
  );

  if (submitted) {
    return (
      <Wrapper>
        <SuccessCard>
          <SuccessIcon>
            <svg
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 24L20 34L38 14"
                stroke="currentColor"
                strokeWidth="2.5"
              />
            </svg>
          </SuccessIcon>
          <SuccessTitle>{successTitle}</SuccessTitle>
          {successMessage && (
            <SuccessMessage
              dangerouslySetInnerHTML={{
                __html: successMessage.replace(/<\/?p>/g, ""),
              }}
            />
          )}
        </SuccessCard>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {cleanTitle && (
        <TitleWrapper>
          <Asterisk>
            <AsteriskSvg />
          </Asterisk>
          <Title dangerouslySetInnerHTML={{ __html: cleanTitle }} />
          {subtitle && <SubTitle>{subtitle}</SubTitle>}
        </TitleWrapper>
      )}

      <FormGrid>
        {fields.map((field) => (
          <FormField
            key={field.name}
            field={field}
            value={values[field.name]}
            error={touched[field.name] ? errors[field.name] : null}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        ))}
      </FormGrid>

      <FormFooter>
        <ErrorMessageArea>
          {(submitError || hasErrors) && (
            <GeneralError>
              <ErrorIcon>
                <ErrorIconCircle>!</ErrorIconCircle>
              </ErrorIcon>
              Sorry, er ging iets mis. Controleer je informatie.
            </GeneralError>
          )}
        </ErrorMessageArea>
        <CTAWrapper>
          <BorderedIcon
            icon="arrow"
            text={ctaText}
            onClick={handleSubmit}
            disabled={hasErrors}
            animateOnScroll={false}
            disableSplitText
          />
        </CTAWrapper>
      </FormFooter>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${util.inner}
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TitleWrapper = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 150rem;
`;

const Asterisk = styled.div`
  position: absolute;
  top: -60rem;
  right: -80rem;
  width: 300rem;
  height: 300rem;
  color: ${({ theme }) => theme.theme.accent};
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }

  ${mediaMobile(css`
    width: 140rem;
    height: 140rem;
    top: -40rem;
    right: -40rem;
  `)}
`;

const Title = styled.h3`
  position: relative;
  text-align: center;
  color: ${({ theme }) => theme.theme.textColor};
  ${setTypography("heading3")}
  font-weight: 100;
  max-width: 800rem;

  strong {
    font-weight: 400;
    font-size: inherit;
    line-height: inherit;
  }
`;

const SubTitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.theme.textColor};
  ${setTypography("smallParagraph")}
  font-weight: 400;
  margin-top: 20rem;
  max-width: 600rem;
  margin-left: auto;
  margin-right: auto;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40rem;
  width: 100%;
  max-width: 1400rem;

  ${mediaMobile(css`
    grid-template-columns: 1fr;
    gap: 30rem;
  `)}
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1400rem;
  margin-top: 60rem;
  gap: 30rem;

  ${mediaMobile(css`
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 20rem;
  `)}
`;

const ErrorMessageArea = styled.div`
  flex: 1;
  min-height: 1px;
`;

const GeneralError = styled.div`
  display: flex;
  align-items: center;
  gap: 12rem;
  color: #e74c3c;
  ${setTypography("smallParagraph")}
`;

const ErrorIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorIconCircle = styled.span`
  width: 36rem;
  height: 36rem;
  min-width: 36rem;
  border-radius: 3px;
  background: #e74c3c;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 22rem;
  line-height: 1;
`;

const CTAWrapper = styled.div`
  flex-shrink: 0;

  .icon-wrapper {
    transform: scale(1) rotateZ(0deg);
    opacity: 1;
  }
`;

const SuccessCard = styled.div`
  background: ${colors.aquaMarine};
  border-radius: 15px;
  padding: 80rem 60rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 1000rem;
  width: 100%;
`;

const SuccessIcon = styled.div`
  width: 60rem;
  height: 60rem;
  margin-bottom: 30rem;

  svg {
    width: 100%;
    height: 100%;
    color: ${colors.outerSpace};
  }
`;

const SuccessTitle = styled.h3`
  ${setTypography("heading3")}
  color: ${colors.outerSpace};
  margin-bottom: 20rem;
`;

const SuccessMessage = styled.p`
  ${setTypography("smallParagraph")}
  color: ${colors.outerSpace};
`;
